import { useState } from 'react'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import TextInput from 'components/Inputs/TextInput'
import DateInput from 'components/Inputs/DateInput'
import AlertDialog from 'components/AlertDialog'
import bankingBillPaymentValidator, {
  normalizeIdentificationField
} from 'validators/bankingBillPaymentValidator'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import { Formik } from 'formik'
import { DateTime } from 'luxon'
import { FaBarcode } from 'react-icons/fa'

import {
  Row,
  Form,
  FormField,
  FieldLabel,
  FormActions,
  HighlightRow,
  CardHeader,
  CardTitle,
  Caption,
  DataList,
  DataRow,
  DataLabel,
  DataValue,
  StatusBadge,
  Divider,
  InfoText,
  WarningText,
  MoneyTextMask,
  PlainMoneyText
} from '../../styles'

const formatDate = date => DateTime.fromISO (date).toFormat ('dd/MM/yyyy')

// Agendamento só faz sentido para hoje em diante.
const isPastDate = ({ date }) => (
  DateTime.fromJSDate (date).startOf ('day') < DateTime.now ().startOf ('day')
)

export default function BillPaymentForm ({
  availableBalance,
  requestConfig,
  onCompleted = () => {}
}) {
  const [simulation, setSimulation] = useState (null)
  const [paying, setPaying] = useState (false)
  const [receipt, setReceipt] = useState (null)
  const [showConfirmDialog, setShowConfirmDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [requestError, setRequestError] = useState (null)

  const toggleConfirmDialog = () => setShowConfirmDialog (prev => !prev)
  const toggleOkDialog = () => setShowOkDialog (prev => !prev)

  /*
    O total debitado é `value + fee`: a API já devolve em `value` o quanto o boleto custa
    hoje, com desconto, juros e multa aplicados. As linhas de desconto/juros/multa abaixo
    são a memória de cálculo, não parcelas a somar — recompô-las aqui daria um total
    diferente do que a API vai cobrar.
  */
  /*
    `hasValue` existe porque uma simulação pode voltar sem valor — foi o que aconteceu com
    um código de barras lido errado pela câmera do app: o Asaas respondeu 200 com o corpo
    vazio, e `Number (undefined)` virou um `R$ NaN` na tela, junto com o aviso de saldo
    insuficiente, que era falso. A API passou a recusar esse caso, e aqui a tela também não
    confia nele.
  */
  const billValue = Number (simulation?.value)
  const hasValue = Number.isFinite (billValue)
  const totalValue = hasValue ? billValue + Number (simulation.fee ?? 0) : 0
  const canPay = hasValue
    && simulation?.canBePaidWithBalance !== false
    && totalValue <= availableBalance

  const payBill = async (values, resetForm) => {
    try {
      setPaying (true)
      setRequestError (null)

      const { data: transaction } = await cutileiApi.post ('/banking_account/bill_payments', {
        identificationField: normalizeIdentificationField (values.identificationField),
        ...values.scheduleDate && {scheduleDate: values.scheduleDate},
        ...values.description && {description: values.description}
      }, requestConfig)

      setReceipt (transaction)
      setSimulation (null)
      // Limpa a linha digitável junto: um boleto pago não pode ser reenviado por engano.
      resetForm ()
      toggleOkDialog ()
      onCompleted ()
    } catch (error) {
      console.log (error.response ?? error)
      setRequestError (
        error.response?.data?.message ?? 'Não foi possível pagar o boleto'
      )
    } finally {
      setPaying (false)
    }
  }

  return (
    <Formik
      validationSchema={bankingBillPaymentValidator}
      initialValues={{
        identificationField: '',
        scheduleDate: null,
        description: ''
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          setSubmitting (true)
          setRequestError (null)

          const { data } = await cutileiApi.post (
            '/banking_account/bill_payments/simulate',
            {identificationField: normalizeIdentificationField (values.identificationField)},
            requestConfig
          )

          setSimulation (data)
          setReceipt (null)
        } catch (error) {
          console.log (error.response ?? error)
          setSimulation (null)
          setRequestError (
            error.response?.data?.message ?? 'Não foi possível consultar o boleto'
          )
        } finally {
          setSubmitting (false)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm
      }) => {
        const digits = normalizeIdentificationField (values.identificationField)

        const handleReset = () => {
          setSimulation (null)
          setRequestError (null)
          resetForm ()
        }

        return (
          <Form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle>Pagar boleto</CardTitle>
              <FaBarcode size={16} color='#FF9800'/>
            </CardHeader>
            <Caption>
              {'Digite ou cole a linha digitável do boleto, ou o código de barras. Você '
              + 'confere os dados antes de confirmar o pagamento'}
            </Caption>
            <Divider/>
            <Row>
              <FormField>
                <FieldLabel>Linha digitável</FieldLabel>
                <TextInput
                  name='identificationField'
                  placeholder='00000.00000 00000.000000 00000.000000 0 00000000000000'
                  value={values.identificationField}
                  disabled={Boolean (simulation)}
                  onChange={handleChange ('identificationField')}
                  onBlur={handleBlur ('identificationField')}
                />
                {touched.identificationField && errors.identificationField ? (
                  <WarningText>{errors.identificationField}</WarningText>
                ) : digits.length > 0 && (
                  <InfoText>{`${digits.length} de 44, 47 ou 48 dígitos`}</InfoText>
                )}
              </FormField>
            </Row>
            {requestError && <WarningText>{requestError}</WarningText>}
            {!simulation ? (
              <FormActions>
                <MainButton
                  label='Consultar boleto'
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  width={180}
                />
              </FormActions>
            ) : (
              <>
                <Divider/>
                <DataList>
                  <DataRow>
                    <DataLabel>Beneficiário</DataLabel>
                    <DataValue>{simulation.companyName ?? 'Não informado'}</DataValue>
                  </DataRow>
                  {simulation.dueDate && (
                    <DataRow>
                      <DataLabel>Vencimento</DataLabel>
                      <DataValue>{formatDate (simulation.dueDate)}</DataValue>
                    </DataRow>
                  )}
                  <DataRow>
                    <DataLabel>Valor do boleto</DataLabel>
                    <MoneyTextMask value={Number (simulation.value)}/>
                  </DataRow>
                  {Number (simulation.discount) > 0 && (
                    <DataRow>
                      <DataLabel>Desconto aplicado</DataLabel>
                      <MoneyTextMask value={Number (simulation.discount)}/>
                    </DataRow>
                  )}
                  {Number (simulation.interest) > 0 && (
                    <DataRow>
                      <DataLabel>Juros aplicados</DataLabel>
                      <MoneyTextMask value={Number (simulation.interest)}/>
                    </DataRow>
                  )}
                  {Number (simulation.fine) > 0 && (
                    <DataRow>
                      <DataLabel>Multa aplicada</DataLabel>
                      <MoneyTextMask value={Number (simulation.fine)}/>
                    </DataRow>
                  )}
                  <DataRow>
                    <DataLabel>Tarifa de pagamento</DataLabel>
                    <MoneyTextMask value={Number (simulation.fee ?? 0)}/>
                  </DataRow>
                </DataList>
                <HighlightRow>
                  <DataLabel><b>Total a pagar</b></DataLabel>
                  <PlainMoneyText value={totalValue}/>
                </HighlightRow>
                <Row>
                  <FormField>
                    <FieldLabel>Agendar para (opcional)</FieldLabel>
                    <DateInput
                      placeholder='Pagar agora'
                      value={values.scheduleDate}
                      clearable
                      disabledDates={isPastDate}
                      onChange={date => setFieldValue ('scheduleDate', date)}
                    />
                  </FormField>
                  <FormField stretch={2}>
                    <FieldLabel>Descrição (opcional)</FieldLabel>
                    <TextInput
                      name='description'
                      placeholder='Aparece no seu extrato'
                      value={values.description}
                      onChange={handleChange ('description')}
                      onBlur={handleBlur ('description')}
                    />
                    {touched.description && errors.description && (
                      <WarningText>{errors.description}</WarningText>
                    )}
                  </FormField>
                </Row>
                {!hasValue && (
                  <WarningText>
                    {'Não foi possível ler o valor deste boleto. Confira o código ou '
                    + 'consulte outro'}
                  </WarningText>
                )}
                {hasValue && !canPay && (
                  <WarningText>
                    {'O total a pagar é maior que o seu saldo disponível'}
                  </WarningText>
                )}
                <FormActions>
                  <SecondaryButton
                    label='Consultar outro'
                    onClick={handleReset}
                    disabled={paying}
                    color='#606060'
                    width={160}
                  />
                  <MainButton
                    label={values.scheduleDate ? 'Agendar pagamento' : 'Pagar'}
                    onClick={toggleConfirmDialog}
                    loading={paying}
                    disabled={!canPay}
                    width={values.scheduleDate ? 180 : 150}
                  />
                </FormActions>
              </>
            )}
            {receipt && (
              <>
                <Divider/>
                <Row style={{justifyContent: 'space-between', alignItems: 'center'}}>
                  <InfoText>
                    {`Último boleto pago${receipt.metadata?.companyName ? ` — ${receipt.metadata.companyName}` : ''}`}
                  </InfoText>
                  <StatusBadge color={BankingService.getTransactionStatusColor (receipt.status)}>
                    {BankingService.getTransactionStatusLabel (receipt.status)}
                  </StatusBadge>
                </Row>
              </>
            )}
            <AlertDialog
              visible={showConfirmDialog}
              title={values.scheduleDate ? 'Confirmar agendamento' : 'Confirmar pagamento'}
              message={
                `${values.scheduleDate ? 'Agendar o pagamento de' : 'Pagar'} `
                + `${totalValue.toLocaleString ('pt-BR', {
                  style: 'currency', currency: 'BRL'
                })} para ${simulation?.companyName ?? 'o beneficiário do boleto'}`
                + `${values.scheduleDate ? ` em ${formatDate (values.scheduleDate)}` : ''}?`
              }
              confirmText={values.scheduleDate ? 'Agendar' : 'Pagar'}
              cancelText='Cancelar'
              onConfirm={() => payBill (values, resetForm)}
              onClose={toggleConfirmDialog}
            />
            <AlertDialog
              visible={showOkDialog}
              title={receipt?.scheduledAt ? 'Pagamento agendado' : 'Boleto pago'}
              message={
                receipt?.scheduledAt
                  ? 'O pagamento será feito na data agendada e o saldo debitado no dia'
                  : 'O pagamento foi enviado ao banco e o saldo já foi debitado'
              }
              confirmText='Ok'
              onClose={toggleOkDialog}
            />
          </Form>
        )
      }}
    </Formik>
  )
}
